import type { DocMeta } from './documents';
import { MAX_WINDOWS, type Rect, type Zone } from './geometry';

/**
 * The open-document session.
 *
 * Documents are open exactly once, in exactly one window. A window owns an
 * ordered group of tabs, its active document, and where it sits on the
 * canvas. One window has focus: it hosts the controls and receives commands.
 */

export interface WindowState {
  id: string;
  /** Tab order. */
  docIds: string[];
  activeId: string | null;
  /** Where the window is shown (for a docked window: its zone's rectangle). */
  rect: Rect;
  zone: Zone | null;
  /** The floating rectangle to return to when a docked window is restored. */
  restore: Rect | null;
  /** Stacking order; higher is in front. */
  z: number;
  /**
   * The logical workspace this window presents: the cloud-synced grouping of its
   * documents, independent of where the window sits. Set lazily, when the window
   * first holds a cloud document. Geometry (rect, zone, z) never leaves the device.
   */
  workspaceId?: string | null;
  /** The account that workspace belongs to: a workspace id is only meaningful (and creatable) for its owner. */
  workspaceAccount?: string | null;
  /**
   * What the person calls this context ("Novel", "Letters"). Device-local, like the
   * window itself; it seeds the cloud workspace's name when that is first created.
   */
  name?: string;
}

export interface SessionState {
  docs: DocMeta[];
  windows: WindowState[];
  focusedId: string;
}

export type SessionAction =
  | { type: 'add'; doc: DocMeta; windowId?: string; activate?: boolean }
  | { type: 'activate'; id: string }
  | { type: 'close'; id: string }
  | { type: 'patch'; id: string; patch: Partial<Omit<DocMeta, 'id'>> }
  | { type: 'reorder'; id: string; index: number }
  | { type: 'move'; id: string; windowId: string }
  | { type: 'openWindow'; window: Omit<WindowState, 'z' | 'docIds' | 'activeId'> & Partial<Pick<WindowState, 'docIds' | 'activeId'>> }
  | { type: 'closeWindow'; id: string }
  | { type: 'focusWindow'; id: string }
  | { type: 'place'; id: string; rect: Rect; zone: Zone | null; restore?: Rect | null }
  | { type: 'placeMany'; rects: Array<{ id: string; rect: Rect }> }
  | { type: 'bindWorkspace'; id: string; workspaceId: string | null; account?: string | null }
  | { type: 'renameWindow'; id: string; name: string }
  | { type: 'reset'; state: SessionState };

export const FIRST_WINDOW = 'w1';

export function emptySession(rect: Rect = { x: 0, y: 0, w: 640, h: 800 }): SessionState {
  return { docs: [], windows: [{ id: FIRST_WINDOW, docIds: [], activeId: null, rect, zone: null, restore: null, z: 1 }], focusedId: FIRST_WINDOW };
}

const topZ = (s: SessionState) => s.windows.reduce((z, w) => Math.max(z, w.z), 0);

function mapWindow(state: SessionState, id: string, fn: (w: WindowState) => WindowState): SessionState {
  let changed = false;
  const windows = state.windows.map((w) => {
    if (w.id !== id) return w;
    const next = fn(w);
    if (next !== w) changed = true;
    return next;
  });
  return changed ? { ...state, windows } : state;
}

/** Bring a window to the front and give it focus. */
function focus(state: SessionState, id: string): SessionState {
  const win = state.windows.find((w) => w.id === id);
  if (!win) return state;
  const front = win.z === topZ(state) && state.windows.filter((w) => w.z === win.z).length === 1;
  if (state.focusedId === id && front) return state;
  const z = front ? win.z : topZ(state) + 1;
  return { ...mapWindow(state, id, (w) => (w.z === z ? w : { ...w, z })), focusedId: id };
}

/** The tab that takes over when `id` leaves a window: the right neighbour, else the left. */
function neighbourAfterRemoval(win: WindowState, id: string): string | null {
  const index = win.docIds.indexOf(id);
  const rest = win.docIds.filter((d) => d !== id);
  if (win.activeId !== id) return win.activeId;
  return rest[index] ?? rest[index - 1] ?? null;
}

export function sessionReducer(state: SessionState, action: SessionAction): SessionState {
  switch (action.type) {
    case 'add': {
      if (state.docs.some((d) => d.id === action.doc.id)) return state;
      const windowId = action.windowId ?? state.focusedId;
      if (!state.windows.some((w) => w.id === windowId)) return state;
      const activate = action.activate ?? true;
      const next = mapWindow({ ...state, docs: [...state.docs, action.doc] }, windowId, (w) => ({
        ...w,
        docIds: [...w.docIds, action.doc.id],
        activeId: activate || !w.activeId ? action.doc.id : w.activeId,
      }));
      return next;
    }
    case 'activate': {
      const win = windowOf(state, action.id);
      if (!win) return state;
      const next = win.activeId === action.id ? state : mapWindow(state, win.id, (w) => ({ ...w, activeId: action.id }));
      return focus(next, win.id);
    }
    case 'close': {
      if (!state.docs.some((d) => d.id === action.id)) return state;
      const win = windowOf(state, action.id);
      const docs = state.docs.filter((d) => d.id !== action.id);
      if (!win) return { ...state, docs };
      return mapWindow({ ...state, docs }, win.id, (w) => ({ ...w, docIds: w.docIds.filter((d) => d !== action.id), activeId: neighbourAfterRemoval(w, action.id) }));
    }
    case 'patch': {
      let changed = false;
      const docs = state.docs.map((d) => {
        if (d.id !== action.id) return d;
        const next = { ...d, ...action.patch };
        changed = (Object.keys(action.patch) as (keyof DocMeta)[]).some((k) => next[k] !== d[k]);
        return changed ? next : d;
      });
      return changed ? { ...state, docs } : state;
    }
    case 'reorder': {
      const win = windowOf(state, action.id);
      if (!win) return state;
      const from = win.docIds.indexOf(action.id);
      const to = Math.max(0, Math.min(win.docIds.length - 1, action.index));
      if (from === to) return state;
      const docIds = win.docIds.filter((d) => d !== action.id);
      docIds.splice(to, 0, action.id);
      return mapWindow(state, win.id, (w) => ({ ...w, docIds }));
    }
    case 'move': {
      const from = windowOf(state, action.id);
      if (!from || from.id === action.windowId || !state.windows.some((w) => w.id === action.windowId)) return state;
      let next = mapWindow(state, from.id, (w) => ({ ...w, docIds: w.docIds.filter((d) => d !== action.id), activeId: neighbourAfterRemoval(w, action.id) }));
      next = mapWindow(next, action.windowId, (w) => ({ ...w, docIds: [...w.docIds, action.id], activeId: action.id }));
      return focus(next, action.windowId);
    }
    case 'openWindow': {
      if (state.windows.length >= MAX_WINDOWS || state.windows.some((w) => w.id === action.window.id)) return state;
      const win: WindowState = { docIds: [], activeId: null, ...action.window, z: topZ(state) + 1 };
      return { ...state, windows: [...state.windows, win], focusedId: win.id };
    }
    case 'closeWindow': {
      // The canvas always keeps one window. Closing a window closes its documents with it
      // (the controller asks about unsaved ones first).
      const win = state.windows.find((w) => w.id === action.id);
      if (!win || state.windows.length === 1) return state;
      const windows = state.windows.filter((w) => w.id !== action.id);
      const docs = state.docs.filter((d) => !win.docIds.includes(d.id));
      let focusedId = state.focusedId;
      if (focusedId === action.id) focusedId = windows.reduce((a, b) => (b.z > a.z ? b : a)).id;
      return { ...state, docs, windows, focusedId };
    }
    case 'focusWindow':
      return focus(state, action.id);
    case 'place':
      return mapWindow(state, action.id, (w) => {
        const restore = action.restore === undefined ? w.restore : action.restore;
        if (w.zone === action.zone && w.restore === restore && sameRectExact(w.rect, action.rect)) return w;
        return { ...w, rect: action.rect, zone: action.zone, restore };
      });
    case 'placeMany': {
      let next = state;
      for (const { id, rect } of action.rects) next = mapWindow(next, id, (w) => ({ ...w, rect, zone: null, restore: null }));
      return next;
    }
    case 'bindWorkspace':
      return mapWindow(state, action.id, (w) =>
        (w.workspaceId ?? null) === action.workspaceId && (w.workspaceAccount ?? null) === (action.account ?? null) ? w : { ...w, workspaceId: action.workspaceId, workspaceAccount: action.account ?? null },
      );
    case 'renameWindow':
      return mapWindow(state, action.id, (w) => ((w.name ?? '') === action.name ? w : { ...w, name: action.name || undefined }));
    case 'reset':
      return action.state;
  }
}

function sameRectExact(a: Rect, b: Rect) {
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

export function windowOf(state: SessionState, docId: string): WindowState | null {
  return state.windows.find((w) => w.docIds.includes(docId)) ?? null;
}

export function focusedWindow(state: SessionState): WindowState {
  return state.windows.find((w) => w.id === state.focusedId) ?? state.windows[0];
}

/** The document the person is working in: the active tab of the focused window. */
export function activeDoc(state: SessionState): DocMeta | null {
  const id = focusedWindow(state)?.activeId;
  return state.docs.find((d) => d.id === id) ?? null;
}

export function docsOf(state: SessionState, win: WindowState): DocMeta[] {
  const byId = new Map(state.docs.map((d) => [d.id, d]));
  return win.docIds.map((id) => byId.get(id)).filter((d): d is DocMeta => !!d);
}

/** Neighbouring document in the focused window, wrapping around. */
export function neighbourId(state: SessionState, direction: 1 | -1): string | null {
  const win = focusedWindow(state);
  if (!win?.docIds.length) return null;
  const index = win.docIds.indexOf(win.activeId ?? '');
  const next = (index + direction + win.docIds.length) % win.docIds.length;
  return win.docIds[next];
}

/** Windows from back to front. */
export function stacking(state: SessionState): WindowState[] {
  return [...state.windows].sort((a, b) => a.z - b.z);
}
